import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Spacer, StandAloneLink, Surface, Txt } from '@vst/beam';
import { useTranslation } from 'react-i18next';

import { selectAddOns } from '@app/redux/addOns';

import { Step1 } from '../ConfigureCardSteps/step-1';
import { Step2 } from '../ConfigureCardSteps/step-2';
import { Step3 } from '../ConfigureCardSteps/step-3';
import { Step4 } from '../ConfigureCardSteps/step-4';
import TermsAndConditions from '../TermsAndConditions';

import styles from '../../styles/configure-voice.module.scss';

const ConfigureVoiceCard = ({
  content,
  handleBack,
  handleSubmit,
  isSubmitDisabled,
  continueButtonLabel,
}: {
  content: string;
  handleBack: () => void;
  handleSubmit: () => void;
  isSubmitDisabled: boolean;
  continueButtonLabel: string;
}) => {
  const { t } = useTranslation('ShopAddons');
  const [openStep, setOpenStep] = useState(1);

  const {
    availableAddons: { productTypes },
    currentShopAddonId,
  } = useSelector(selectAddOns);
  const currentAddon = productTypes?.find((el) => el.id === currentShopAddonId);

  const catalogDisclosure =
    currentAddon?.marketing_copy.translations[0].characteristics?.find(
      (characteristic) => characteristic.name === 'DISCLOSURE'
    )?.value ?? '';

  const stepProps = { openStep, setOpenStep };

  return (
    <Surface responsive className={styles.content}>
      <Txt variant={'bodyLargeRegular'}>{t('voice.cardHeader')}</Txt>
      <>
        <Step1 isOpen={openStep === 1} {...stepProps} />
        <Step2 isOpen={openStep === 2} {...stepProps} />
        <Step3 isOpen={openStep === 3} {...stepProps} />
        <Step4 isOpen={openStep === 4} {...stepProps} />
      </>
      <Txt mt="40px" mb="8px" variant="smallRegular" color="subtle" component="p">
        {catalogDisclosure}
      </Txt>
      <TermsAndConditions content={content} />
      <Spacer y={'48px'} />
      <Surface>
        <hr className={styles.divider} />
        <Actions
          submit={handleSubmit}
          back={handleBack}
          isSubmitDisabled={isSubmitDisabled}
          continueButtonLabel={continueButtonLabel}
        />
      </Surface>
    </Surface>
  );
};

export default ConfigureVoiceCard;

const Actions = ({
  submit,
  back,
  isSubmitDisabled,
  continueButtonLabel,
}: {
  submit: () => void;
  back: () => void;
  isSubmitDisabled: boolean;
  continueButtonLabel: string;
}) => {
  const { t } = useTranslation('ShopAddons');

  const handleBack = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    back();
  };

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    submit();
  };

  return (
    <div className={styles.buttons}>
      <StandAloneLink variant="primary" linkSize="medium" href={'#'} data-cy="navigatorBackLink" onClick={handleBack}>
        {t('goBack')}
      </StandAloneLink>
      <Button
        variant="primary"
        fluid
        type="submit"
        data-cy="navigatorContinueButton"
        onClick={handleSubmit}
        disabled={isSubmitDisabled}
      >
        {continueButtonLabel}
      </Button>
    </div>
  );
};
